/**
 * Task priority, two levels (Akash, 2026-10-31): Normal and High.
 *
 * Stored as the int it always was (`TaskRequest.priority`, a favourite's
 * `priority`, a schedule's or an override's task): 0 Normal, 1 High. RMF's
 * planner honours exactly two levels, so High means "goes ahead of the
 * waiting jobs; it does not stop the job the robot is doing now".
 *
 * ONE RULE FOR EVERY STORED VALUE, the twin of `priority_level()` in
 * `backend/v1_contract/v1_contract/schemas.py` (keep them in step; a test
 * pins the cases). Exactly the integer 1 is High. Everything else is
 * Normal: null, a bool, a string, NaN, 0 and below -- and 2 to 5, which
 * the old "Priority (1-5)" field stored for any bump. Those never had an
 * effect at the robots, and High now needs a supervisor, so an old front
 * desk bump must not start jumping the queue.
 *
 * Read EVERY value through it, not only the ones this console wrote:
 * `GET /tasks/{id}/request` builds its answer without the api-server's
 * validator, an older api-server returns the raw value, and command
 * history keeps the old numbers forever.
 */
import { ApiClientError } from './v1-client';

export const PRIORITY_NORMAL = 0;
export const PRIORITY_HIGH = 1;

export type PriorityLevel = typeof PRIORITY_NORMAL | typeof PRIORITY_HIGH;

/** A stored priority as its level: 1 for exactly the integer 1, else 0. */
export function priorityLevel(value: unknown): PriorityLevel {
  return typeof value === 'number' && value === PRIORITY_HIGH ? PRIORITY_HIGH : PRIORITY_NORMAL;
}

export function isHighPriority(value: unknown): boolean {
  return priorityLevel(value) === PRIORITY_HIGH;
}

/**
 * The api-server's refusal of High from someone who may not set it: 403
 * `capability_denied` naming the field (`task_priority.py`). Its message
 * is already in the person's words, so callers show it as it stands.
 */
export function isHighPriorityRefusal(err: unknown): err is ApiClientError {
  if (!(err instanceof ApiClientError) || err.status !== 403) return false;
  // FastAPI nests the envelope under `detail`, and the envelope's own
  // `detail` carries the field; read either depth.
  const envelope = err.detail as { field?: unknown; detail?: { field?: unknown } } | undefined;
  return envelope?.detail?.field === 'priority' || envelope?.field === 'priority';
}
